import { forwardRef, useId, type SelectHTMLAttributes } from 'react'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/internationalization/cn'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  label?: string
  hint?: string
  error?: string
  options: SelectOption[]
  placeholder?: string
}

/**
 * A native <select>, deliberately.
 *
 * On mobile the OS picker beats any custom listbox we would write, and it is
 * accessible for free. Use Dropdown only when the options need icons, avatars
 * or a two-line layout that <option> cannot render.
 */
export const Select = forwardRef<HTMLSelectElement, SelectProps>(function Select(
  { label, hint, error, options, placeholder, className, id, ...props },
  ref,
) {
  const autoId = useId()
  const selectId = id ?? autoId
  return (
    <div className="w-full">
      {label && (
        <label htmlFor={selectId} className="mb-1.5 block text-sm font-medium text-fg">
          {label}
        </label>
      )}
      <div className="relative">
        <select
          ref={ref}
          id={selectId}
          aria-invalid={error ? true : undefined}
          className={cn(
            'h-10 w-full appearance-none rounded-md border border-border bg-surface pl-3 pr-9',
            'text-base text-fg transition-colors duration-fast hover:border-border-strong',
            'disabled:cursor-not-allowed disabled:bg-surface-muted',
            error && 'border-danger',
            className,
          )}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>
        <ChevronDown
          aria-hidden
          className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-subtle"
        />
      </div>
      {(error || hint) && (
        <p className={cn('mt-1.5 text-sm', error ? 'text-danger' : 'text-fg-subtle')}>{error ?? hint}</p>
      )}
    </div>
  )
})
